import { useEffect, useMemo, useState } from 'react';
import {
  resolveCreditsMeter,
  resolveDailyLimitMeter,
  useCredits,
  type CreditsMeter,
  type DailyLimitMeter,
} from './use-credits';
import { useCreditPlanContext } from './use-credit-plan-context';
import { creditsStateBiFields } from './credits-state-bi';
import { isFreeUsageActive } from './free-usage';
import { useFreeUsageUntil } from './use-free-usage';
import {
  useCreditsWidgetSim,
  useCreditsWidgetSimDelay,
  useCreditsWidgetSimMessageCount,
  SIM_DELAY_MESSAGE_THRESHOLD,
  type CreditsWidgetSim,
} from './credits-widget-sim';

/**
 * The one source of truth for whether — and how — the low/out-of-credits widgets show. The drawer
 * card, the inner-screen banner and the in-chat card + sticky bar all read THIS, so no two surfaces can
 * disagree about whether the account is low, out, or fine, or which pool they measured.
 *
 * `none` — fine, or the widgets are gated off, or the balance is unknown. The widgets render nothing.
 * `low`  — 70%+ of the relevant pool is spent (but some remains). Nudge to upgrade / buy more.
 * `out`  — the relevant pool is exhausted (0 left). Harder stop.
 */
export type CreditsWidgetLevel = 'none' | 'low' | 'out';

/**
 * Which pool the state was measured against — it picks WHICH copy the widget speaks. `daily` = the
 * per-day ceiling (the BFF sent a `dailyLimit`, which only a free plan gets), whose copy nudges to a
 * Premium plan and names no plan/date. `monthly` = the period pool, whose copy splits by tier (free /
 * paid / Max+). A free plan under a daily ceiling can read either: see `pickCreditsWidgetPool`.
 */
export type CreditsWidgetPool = 'daily' | 'monthly';

/** The used-percent at or above which the state is "low". A product constant, deliberately not a
 *  request field — mirrors the native `LOW_CREDITS_THRESHOLD_PCT` twin. */
export const LOW_CREDITS_THRESHOLD_PCT = 70;

/**
 * How close to the monthly renewal the "low" state stops showing. Inside this window "you're almost out —
 * upgrade before your credits renew on <today>" is noise: the allotment refills within hours whatever the
 * owner does. Reported by a Max-plan owner at 90% used with 955 credits left and the reset five hours away.
 * MONTHLY only — the daily ceiling always resets within a day, so applying it there would delete the daily
 * low state outright. OUT is never quieted: an empty pool stops the agents now, not after the reset.
 * Mirrors the native `CREDITS_WIDGET_LOW_RENEWAL_QUIET_MS` / `creditsWidgetLowRenewalQuietInterval` twins.
 */
export const LOW_CREDITS_RENEWAL_QUIET_MS = 24 * 60 * 60 * 1000;

export interface CreditsWidgetState {
  level: CreditsWidgetLevel;
  pool: CreditsWidgetPool;
  /** Free (unpaid) plan. Its MONTHLY copy names no plan/date, and its CTA reads "Upgrade". (Daily is
   *  always the free plan too, so daily copy never depends on this.) */
  isFreePlan: boolean;
  /** Top ("Max+") tier. Its monthly copy is the date-free "Add credits…" body; CTA reads "Add Credits". */
  isMaxPlus: boolean;
  /**
   * Whole-number percent of the relevant pool SPENT — the menu card's bar width. Always shows the real
   * value (≈70+ when low), forced to 100 when out (the pool is exhausted, so the bar is full). 0 when
   * `level` is `none`.
   */
  usedPct: number;
  /** Credits remaining in the relevant pool (0 when out). */
  remaining: number;
  /** Credits SPENT in the relevant pool, for the "{used} of {cap}" line above the bar. NOT clamped to
   *  `cap` (an over-limit day reads "105 of 100"); 0 when `level` is `none`. */
  used: number;
  /** The relevant pool's allotment (monthly `cap` / daily `limit`), for that same line. 0 when unknown. */
  cap: number;
  /** The account's plan name, for the monthly "…on your {planName} plan" copy. Empty on daily/free. */
  planName: string;
  /** Formatted renewal date for the monthly body, or null → the `.noDate` body. Null on daily/free. */
  renewalDate: string | null;
  /**
   * The plan's own allotment is spent and everything still spendable is the non-resetting bucket.
   *
   * It exists because the ordinary monthly copy makes a claim about the RESET — "upgrade so your
   * agents don't pause before your credits renew on {date}" — and for this account the claim is
   * false twice over: they are not *almost* out of monthly credits, they are out of them; and what
   * remains is not going anywhere on that date. Reported by an owner holding 296 such credits at 87%
   * of the combined pool, who read it as being nagged to pay for something he already had.
   *
   * Says nothing about where those credits came from, because {@link CreditAccount.topupRemaining}
   * cannot: it holds purchases and goodwill grants alike. The copy it selects must not either.
   */
  topupOnly: boolean;
}

/** What `creditsWidgetCopyKeys` returns: the title/body keys plus the values to interpolate into them. */
export interface CreditsWidgetCopy {
  titleKey: string;
  bodyKey: string;
  /** Interpolation values for the monthly (paid/Max+) copy; empty on daily. */
  params: { planName?: string; renewalDate?: string };
}

/**
 * The title/body i18n keys (and interpolation params) for a state. Pure so it is unit-tested.
 *
 *  - `daily` → the free plan's daily copy (no plan name / no date). Daily is always the free plan.
 *  - `monthly` splits three ways by tier:
 *     · Max+   → the shared monthly TITLE (naming the plan) + a single date-free "Add credits…" body.
 *     · free   → its OWN title + body, naming NO plan and NO date ("…out of monthly credits").
 *     · paid   → the plan-named title + a body naming the renewal date (the `.noDate` body otherwise).
 *
 * The CTA label also varies by tier (see `useCreditsWidgetCta`). Never called for `none`.
 */
export function creditsWidgetCopyKeys(state: CreditsWidgetState): CreditsWidgetCopy {
  const lvl = state.level === 'out' ? 'out' : 'low';
  if (state.pool === 'daily') {
    return { titleKey: `credits.widget.daily.${lvl}.title`, bodyKey: `credits.widget.daily.${lvl}.body`, params: {} };
  }
  if (state.isMaxPlus) {
    // One date-free body for both levels; the level still selects the title.
    return { titleKey: `credits.widget.monthly.${lvl}.title`, bodyKey: 'credits.widget.monthly.maxplus.body', params: { planName: state.planName } };
  }
  if (state.isFreePlan) {
    return { titleKey: `credits.widget.monthly.free.${lvl}.title`, bodyKey: `credits.widget.monthly.free.${lvl}.body`, params: {} };
  }
  // The plan's allotment is spent and the remainder does not reset, so neither half of the paid copy
  // below is true: "almost out of monthly credits" understates it (there are none), and "before your
  // credits renew on {date}" points at a date these credits ignore. Names no plan and no date, and
  // deliberately does not say whether they were bought — `topupOnly` cannot know, see its docblock.
  // The CTA is untouched: `useCreditsWidgetCta` offers "Add Credits" only to Max+, and Max+ is
  // already handled above, so this branch keeps Upgrade, which is the action that actually exists.
  if (state.topupOnly) {
    return { titleKey: 'credits.widget.monthly.topup.low.title', bodyKey: 'credits.widget.monthly.topup.low.body', params: {} };
  }
  return {
    titleKey: `credits.widget.monthly.${lvl}.title`,
    bodyKey: state.renewalDate ? `credits.widget.monthly.${lvl}.body` : `credits.widget.monthly.${lvl}.body.noDate`,
    params: { planName: state.planName, renewalDate: state.renewalDate ?? '' },
  };
}

/** Format a renewal date (ISO) for the monthly body, or null when absent/unparseable → `.noDate` copy. */
export function formatRenewalDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export interface CreditsWidgetInputs {
  /** Today's ceiling as a meter, or null when the account is under no per-day cap. */
  dailyMeter: DailyLimitMeter | null;
  /** The period (monthly) meter, or null when the balance is unknown. */
  periodMeter: CreditsMeter | null;
  /** Free (unpaid) plan — picks the free monthly copy + the "Upgrade" CTA. */
  isFreePlan: boolean;
  /** Top ("Max+") tier — the date-free "Add credits…" body + the "Add Credits" CTA. */
  isMaxPlus: boolean;
  /** The account's plan name, for the monthly copy. */
  planName: string;
  /** Formatted renewal date for the monthly copy, or null → the `.noDate` body. */
  renewalDate: string | null;
  /** The non-resetting half of the period meter's balance (`CreditAccount.topupRemaining`). Absent
   *  reads as 0, which keeps every existing caller on exactly today's copy. */
  topupRemaining?: number | null;
  /** When the monthly allotment renews (ISO), or null when unknown — a LOW monthly state inside
   *  `LOW_CREDITS_RENEWAL_QUIET_MS` of it reads as none. Absent keeps today's behaviour. */
  renewalDateIso?: string | null;
  /** End of an open free-usage window (promo free hours), ISO. While it is in the future nothing is
   *  charged, so the state is `none` whatever the meters read. */
  freeUsageUntilIso?: string | null;
  /** The clock, injectable for tests. */
  now?: number;
}

/** The plan facts carried onto every classified state (only rendered on monthly). */
type PlanCtx = Pick<CreditsWidgetState, 'isFreePlan' | 'isMaxPlus' | 'planName' | 'renewalDate'>;

const NONE: CreditsWidgetState = { level: 'none', pool: 'monthly', isFreePlan: true, isMaxPlus: false, usedPct: 0, remaining: 0, used: 0, cap: 0, planName: '', renewalDate: null, topupOnly: false };

/**
 * Is every credit still spendable one that survives the reset?
 *
 * Deliberately strict: the plan's own allotment must be GONE. With periodic credits still in hand the
 * ordinary copy is true (those really do run out before the renewal), so it stays — this only replaces
 * the sentence in the one state where it is false. MONTHLY only; the daily ceiling is a second pool
 * with its own rule and a top-up buys no relief from it.
 */
function isTopupOnly(pool: CreditsWidgetPool, remaining: number, topupRemaining: number): boolean {
  return pool === 'monthly' && topupRemaining > 0 && remaining - topupRemaining <= 0;
}

function classify(pool: CreditsWidgetPool, usedPct: number, remaining: number, used: number, cap: number, topupRemaining: number, ctx: PlanCtx): CreditsWidgetState {
  const pct = Math.min(100, Math.max(0, usedPct));
  // Out first: an exhausted pool is "out" even if a rounding quirk left usedPct just under 100, and the
  // bar is drawn FULL there rather than at the real percent (product: "when out — totally full").
  //
  // `topupOnly` is false here by construction rather than by omission: nothing is left to survive the
  // reset, so the out copy is honest as it stands.
  if (remaining <= 0) return { level: 'out', pool, usedPct: 100, remaining: 0, used, cap, topupOnly: false, ...ctx };
  if (pct >= LOW_CREDITS_THRESHOLD_PCT) {
    return { level: 'low', pool, usedPct: pct, remaining, used, cap, topupOnly: isTopupOnly(pool, remaining, topupRemaining), ...ctx };
  }
  return { ...NONE, pool, usedPct: pct, remaining, used, cap, ...ctx };
}

/**
 * Which pool the credits surfaces measure — the widget state, and the drawer's plain card bar with it.
 *
 * An account with no daily ceiling has one pool: MONTHLY. An account with both shows the one it has
 * spent the larger share of, and a tie goes to DAILY. An EXHAUSTED month always wins, whatever today
 * reads: the BFF reports today's `remaining` as `limit - used` with no regard for the monthly balance,
 * so "today" can look open on an account that cannot spend at all — and waiting for tomorrow, which is
 * what the daily copy implies, does not help it. With no known monthly balance, only today can be
 * measured.
 *
 * The comparison is on exact shares (cross-multiplied, no rounding), each clamped to 0..1: a daily
 * overrun ("105 of 100") and a monthly balance above its cap (top-up) must not win or lose by more
 * than a full or an empty bar. Mirrors native `pickCreditsWidgetPool`.
 */
export function pickCreditsWidgetPool(daily: DailyLimitMeter | null, period: CreditsMeter | null): CreditsWidgetPool {
  if (!daily) return 'monthly';
  if (!period || period.usedPct === null) return 'daily';
  if (period.balance <= 0) return 'monthly';
  const dailyUsed = Math.min(daily.limit, Math.max(0, daily.used));
  const monthlyUsed = Math.min(period.cap, Math.max(0, period.used));
  return dailyUsed * period.cap >= monthlyUsed * daily.limit ? 'daily' : 'monthly';
}

/**
 * Decide the widget state from the two meters. Pure, so every branch (pool selection, the 70%/0
 * thresholds, the gate) is unit-tested without React. The pool comes from `pickCreditsWidgetPool`;
 * the level (out → low → none) is then read off that pool alone.
 *
 * A missing period meter (unknown balance) with no daily ceiling yields `none`: there is nothing honest
 * to show, the same fail-closed reading every other credits surface takes.
 */
export function computeCreditsWidgetState(inputs: CreditsWidgetInputs): CreditsWidgetState {
  const ctx: PlanCtx = { isFreePlan: inputs.isFreePlan, isMaxPlus: inputs.isMaxPlus, planName: inputs.planName, renewalDate: inputs.renewalDate };
  if (isFreeUsageActive(inputs.freeUsageUntilIso, inputs.now ?? Date.now())) return { ...NONE, ...ctx };
  const daily = inputs.dailyMeter;
  const period = inputs.periodMeter;
  if (daily && pickCreditsWidgetPool(daily, period) === 'daily') {
    return classify('daily', daily.usedPct, daily.remaining, daily.used, daily.limit, 0, ctx);
  }
  if (!period || period.usedPct === null) return NONE;
  const state = classify('monthly', period.usedPct, period.balance, period.used, period.cap, Math.max(0, inputs.topupRemaining ?? 0), ctx);
  return state.level === 'low' && renewsWithinQuietWindow(inputs.renewalDateIso, inputs.now ?? Date.now())
    ? { ...state, level: 'none', topupOnly: false }
    : state;
}

/** The longest delay `setTimeout` honours (2^31-1 ms, ~24.8 days); a longer one fires at once. */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * How long until the renewal's quiet window opens — the instant a LOW state must re-classify as none —
 * or null when there is no such boundary ahead (no renewal date, or already inside the window). Nothing
 * else changes at that instant, so without a timer a view loaded earlier keeps showing LOW across it.
 * Clamped to what `setTimeout` can hold; the caller re-arms when a clamped timer fires.
 */
export function creditsQuietWindowDelayMs(renewalDateIso: string | null | undefined, now: number): number | null {
  if (!renewalDateIso) return null;
  const renewsAt = new Date(renewalDateIso).getTime();
  if (!Number.isFinite(renewsAt)) return null;
  // +1: the window is `renewsAt - now < QUIET` (strict), so exactly QUIET out is still LOW — wake on the
  // first instant that is quiet. Mirrors the native `creditsQuietWindowDelayMs` twins.
  const delay = renewsAt - LOW_CREDITS_RENEWAL_QUIET_MS - now + 1;
  return delay > 0 ? Math.min(delay, MAX_TIMER_DELAY_MS) : null;
}

/** Whether the monthly renewal is due inside the quiet window. A renewal already behind us (a balance read
 *  before the reset landed) counts as due: the refill is imminent, not a month away. */
function renewsWithinQuietWindow(renewalDateIso: string | null | undefined, now: number): boolean {
  if (!renewalDateIso) return false;
  const renewsAt = new Date(renewalDateIso).getTime();
  return Number.isFinite(renewsAt) && renewsAt - now < LOW_CREDITS_RENEWAL_QUIET_MS;
}

/** A sample renewal date (~2 weeks out) so the dev sim's monthly states preview the WITH-date copy. */
function simRenewalDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return formatRenewalDate(d.toISOString()) ?? '';
}

/** Map a dev simulator choice onto a concrete widget state (sample numbers). Dev-only. The monthly
 *  variants differ only in tier (free / paid / Max+), which changes the copy + the CTA. */
export function simToCreditsWidgetState(sim: CreditsWidgetSim): CreditsWidgetState {
  const free = { isFreePlan: true, isMaxPlus: false, planName: '', renewalDate: null };
  const paid = { isFreePlan: false, isMaxPlus: false, planName: 'Pro', renewalDate: simRenewalDate() };
  const maxPlus = { isFreePlan: false, isMaxPlus: true, planName: 'Max+', renewalDate: null };
  switch (sim) {
    case 'low-daily':
      return { level: 'low', pool: 'daily', usedPct: LOW_CREDITS_THRESHOLD_PCT, remaining: 30, used: 70, cap: 100, topupOnly: false, ...free };
    case 'out-daily':
      return { level: 'out', pool: 'daily', usedPct: 100, remaining: 0, used: 100, cap: 100, topupOnly: false, ...free };
    case 'low-monthly-free':
      return { level: 'low', pool: 'monthly', usedPct: LOW_CREDITS_THRESHOLD_PCT, remaining: 300, used: 700, cap: 1000, topupOnly: false, ...free };
    case 'out-monthly-free':
      return { level: 'out', pool: 'monthly', usedPct: 100, remaining: 0, used: 1000, cap: 1000, topupOnly: false, ...free };
    case 'low-monthly':
      return { level: 'low', pool: 'monthly', usedPct: LOW_CREDITS_THRESHOLD_PCT, remaining: 300, used: 700, cap: 1000, topupOnly: false, ...paid };
    case 'out-monthly':
      return { level: 'out', pool: 'monthly', usedPct: 100, remaining: 0, used: 1000, cap: 1000, topupOnly: false, ...paid };
    case 'low-monthly-maxplus':
      return { level: 'low', pool: 'monthly', usedPct: LOW_CREDITS_THRESHOLD_PCT, remaining: 300, used: 700, cap: 1000, topupOnly: false, ...maxPlus };
    case 'out-monthly-maxplus':
      return { level: 'out', pool: 'monthly', usedPct: 100, remaining: 0, used: 1000, cap: 1000, topupOnly: false, ...maxPlus };
  }
}

/**
 * The live low/out-of-credits state for the current account. Built on the shared `useCredits()` cache,
 * so it reacts to mid-conversation spend on its own (the `individuals-credits` duplexer push).
 *
 * A dev-tools simulator (`useCreditsWidgetSim`) substitutes the credit DATA (so the widgets can be
 * previewed on any account). Inert without a dev-tools grant.
 *
 * Kept as a thin alias of `useCreditsState` (its historical role was to AND that state with the
 * now-graduated `symphonyOutOrLowCreditsWidgetsWeb` rollout flag) so its many consumers need no edit.
 */
export function useCreditsWidgetState(): CreditsWidgetState {
  return useCreditsState();
}

/**
 * The account's credit state — the SINGLE source of truth for the low/out-of-credits widgets. BI reads
 * THIS directly (`useCreditsBiFields`) so a personal-sale / credits click reports the same credit
 * situation everywhere. The dev simulator still applies here (dev-only; inert in production, where no
 * sim is ever set). Mirrors native `creditsState`.
 */
export function useCreditsState(): CreditsWidgetState {
  const credits = useCredits();
  const sim = useCreditsWidgetSim();
  const simDelay = useCreditsWidgetSimDelay();
  const simMsgCount = useCreditsWidgetSimMessageCount();
  const { isFreePlan, isMaxPlus, planName, renewalDateIso } = useCreditPlanContext();
  const daily = resolveDailyLimitMeter(credits);
  const period = resolveCreditsMeter(credits);
  const topupRemaining = credits.account?.topupRemaining ?? 0;
  const freeUsageUntilIso = useFreeUsageUntil();
  // Re-classify at the moment the renewal's quiet window opens (see `creditsQuietWindowDelayMs`).
  const [quietWindowTick, setQuietWindowTick] = useState(0);
  useEffect(() => {
    const delay = creditsQuietWindowDelayMs(renewalDateIso, Date.now());
    if (delay === null) return undefined;
    const timer = setTimeout(() => setQuietWindowTick((tick) => tick + 1), delay);
    return () => clearTimeout(timer);
  }, [renewalDateIso, quietWindowTick]);
  // Keyed on the primitives the state depends on, so the returned object is stable across renders that
  // did not move the balance — consumers don't re-render on unrelated credits reads.
  return useMemo(() => {
    if (sim) {
      // "Appear after N messages" mode: hold the state back until enough messages have been sent, to
      // reproduce credits running out mid-conversation.
      if (simDelay && simMsgCount < SIM_DELAY_MESSAGE_THRESHOLD) return NONE;
      return simToCreditsWidgetState(sim);
    }
    return computeCreditsWidgetState({
      dailyMeter: daily,
      periodMeter: period,
      isFreePlan,
      isMaxPlus,
      planName,
      renewalDate: formatRenewalDate(renewalDateIso),
      topupRemaining,
      renewalDateIso,
      freeUsageUntilIso,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sim, simDelay, simMsgCount, freeUsageUntilIso, isFreePlan, isMaxPlus, planName, renewalDateIso, quietWindowTick, daily?.usedPct, daily?.used, daily?.limit, daily?.remaining, period?.usedPct, period?.balance, period?.cap, topupRemaining]);
}

/**
 * The credits-state BI additional fields (`credits_state` / `credits_pool` / `plan_tier`) attached to
 * every personal-sale / credits CTA event, from the FT-independent `useCreditsState` — see
 * `creditsStateBiFields`. One source of truth so all three events agree across all feature-flag states.
 */
export function useCreditsBiFields(): Record<string, string> {
  const s = useCreditsState();
  return creditsStateBiFields({ level: s.level, pool: s.pool, isFreePlan: s.isFreePlan, isMaxPlus: s.isMaxPlus });
}
